/**
 * The one bit of state that separates a look from a click.
 *
 * A left-button press that turns into a drag is a look — the gallery uses that gesture to
 * scroll the images past the planet, and the lobby's fallback for looking around, when the
 * pointer lock is refused or unavailable, is hold-the-button-and-drag. A press with no
 * movement in it is a click, and clicks select things.
 *
 * The click event that ends a gesture arrives *after* the mouseup, so the drag cannot be
 * cleared when the button comes up — the click still has to be able to ask whether it was
 * the tail of a drag. That is why the drag outlives `release()`, and why the only thing
 * that clears it is a click consuming it.
 *
 * This exists as a module because of how the flag it replaces was cleared. It was reset
 * inside one branch of the click handler — the branch that only runs once the doors are
 * open — so every branch that returned before reaching it left the flag set. The lobby's
 * door ray returns early by design, so the drag that aimed the reticle at the button
 * survived into the gallery, and the first click on a planet was swallowed as if it had
 * been a drag. One dead click, then everything normal: the latch was not broken, it was
 * simply never consulted on the path that happened to run. A latch only some paths reset
 * is not a latch.
 *
 * So the contract is: **every click consumes the latch, before any branch can act on it.**
 * Read it once at the top of the handler and pass the answer down, rather than testing and
 * clearing it where it is used.
 */
export function createGestureLatch() {
    let pressed = false;
    let dragging = false;

    return {
        /** The left button went down. A new gesture always starts undragged. */
        press() {
            pressed = true;
            dragging = false;
        },

        /**
         * The pointer moved. Any movement at all while the button is held makes the whole
         * gesture a drag — there is no threshold, because the scroll gesture is a slow
         * deliberate one and a pixel of travel is enough to mean it.
         */
        move() {
            if (pressed) dragging = true;
        },

        /** The left button came up. The drag, if there is one, is kept for the click. */
        release() {
            pressed = false;
        },

        /**
         * Reads and clears the drag in one step. `true` means the click that just arrived
         * was the end of a look and should not select anything.
         *
         * Deliberately a consume rather than a read: calling it twice must not report the
         * same drag twice, or a swallowed click would swallow the one after it as well.
         */
        consumeClick() {
            const wasDragging = dragging;
            dragging = false;
            return wasDragging;
        },

        isPressed() {
            return pressed;
        },

        isDragging() {
            return dragging;
        }
    };
}
